import { defineElement, PUB_SUB_EVENTS } from "./gw.js";
import { subscribe } from "./pubsub.js";

const ADD_TIMEOUT = 10000;

class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.productForm = document.querySelector(
      `#MainProduct-${this.dataset.sectionId} product-form`,
    );
    this.form = this.productForm?.querySelector("form");
    if (!this.form) return;

    this.bar = this.querySelector(".sticky-atc__bar");
    this.button = this.querySelector(".sticky-atc__button");
    this.sheet = this.querySelector("dialog");
    this.errorMessage = this.querySelector("[data-sticky-atc-error]");

    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick);

    // The root extends far below the viewport so the form only stops
    // intersecting once it is scrolled past, even after jumps (anchor links).
    this.observer = new IntersectionObserver(
      ([entry]) => this.toggleVisibility(!entry.isIntersecting),
      { rootMargin: "0px 0px 100000px 0px" },
    );
    this.observer.observe(this.productForm);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.observer?.disconnect();
    this.finishAdd();
    document.body.classList.remove("sticky-atc-visible");
  }

  onClick(event) {
    if (event.target === this.sheet || event.target.closest("[data-sticky-atc-close]")) {
      this.sheet.close();
      return;
    }

    const variantButton = event.target.closest("[data-variant-id]");
    if (variantButton) {
      this.addToCart(variantButton);
      return;
    }

    if (event.target.closest(".sticky-atc__button")) this.openSheet();
  }

  toggleVisibility(visible) {
    this.classList.toggle("is-visible", visible);
    this.bar.inert = !visible;
    document.body.classList.toggle(
      "sticky-atc-visible",
      visible && this.getClientRects().length > 0,
    );
  }

  openSheet() {
    this.showError();
    this.sheet.showModal();
    this.sheet.querySelector(".sticky-atc__size:not(:disabled)")?.focus();
  }

  addToCart(source) {
    if (this.pendingVariantId) return;
    if (this.productForm.submitButton?.getAttribute("aria-disabled") === "true") return;

    this.pendingVariantId = source.dataset.variantId;
    this.pendingSource = source;
    this.setLoading(true);
    this.showError();
    this.unsubscribers = [
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this)),
      subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this)),
    ];
    this.addTimeout = setTimeout(() => this.finishAdd(), ADD_TIMEOUT);

    // product-form builds its FormData synchronously inside the submit event,
    // so the main form can point at this variant and be restored right after.
    const idInput = this.form.querySelector('input[name="id"]');
    const propertyInputs = this.form.querySelector("[data-property-inputs]");
    const previousId = idInput.value;
    const previousDisabled = idInput.disabled;
    const previousProperties = propertyInputs ? [...propertyInputs.children] : [];

    idInput.value = this.pendingVariantId;
    idInput.disabled = false;
    propertyInputs?.replaceChildren(...this.getPropertyInputs(source));

    this.form.requestSubmit();

    idInput.value = previousId;
    idInput.disabled = previousDisabled;
    propertyInputs?.replaceChildren(...previousProperties);

    document.querySelector("cart-drawer")?.setActiveElement(this.button);
  }

  getPropertyInputs({ dataset }) {
    if (!("preorder" in dataset)) return [];

    const properties = [[window.variantStrings.saleType, window.variantStrings.preorder]];
    if (dataset.preorderMessage) {
      properties.push(["_preorder_message", dataset.preorderMessage]);
    }

    return properties.map(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${name}]`;
      input.value = value;
      return input;
    });
  }

  onCartUpdate({ source, productVariantId }) {
    if (source !== "product-form" || String(productVariantId) !== this.pendingVariantId) return;
    this.finishAdd();
    this.sheet?.close();
  }

  onCartError({ source, productVariantId, errors, message }) {
    if (source !== "product-form" || String(productVariantId) !== this.pendingVariantId) return;
    this.finishAdd();
    this.showError(typeof errors === "string" ? errors : message);
  }

  finishAdd() {
    clearTimeout(this.addTimeout);
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = null;
    if (this.pendingSource) this.setLoading(false);
    this.pendingVariantId = null;
    this.pendingSource = null;
  }

  setLoading(loading) {
    this.pendingSource.classList.toggle("loading", loading);
    this.pendingSource
      .querySelector(".loading__spinner")
      ?.classList.toggle("hidden", !loading);
    [this.pendingSource, this.sheet].forEach((element) => {
      if (!element) return;
      if (loading) element.setAttribute("aria-busy", "true");
      else element.removeAttribute("aria-busy");
    });
  }

  showError(message = "") {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message;
    this.errorMessage.hidden = !message;
  }
}

defineElement("sticky-atc", StickyAtc);
